/**
 * ShareLinkCard Component (Story 14.2)
 *
 * Single share-link row with status pill, scope badge, prefix, expires date,
 * access count, and management actions (edit/regenerate/revoke).
 */

import {
  PencilSimple,
  ArrowsClockwise,
  Trash,
  Lock,
  Eye,
} from "@phosphor-icons/react";
import { Button, Badge } from "@provenleap/ui";
import {
  formatExpirationLabel,
  type PortalLinkProjection,
} from "../../lib/portalLinksApi";

export interface ShareLinkCardProps {
  link: PortalLinkProjection;
  canManage: boolean;
  onEdit: () => void;
  onRegenerate: () => void;
  onRevoke: () => void;
}

function statusVariant(status: PortalLinkProjection["status"]) {
  switch (status) {
    case "active":
      return "default";
    case "expired":
      return "secondary";
    case "revoked":
      return "destructive";
  }
}

function statusLabel(status: PortalLinkProjection["status"]) {
  switch (status) {
    case "active":
      return "Active";
    case "expired":
      return "Expired";
    case "revoked":
      return "Revoked";
  }
}

export function ShareLinkCard({
  link,
  canManage,
  onEdit,
  onRegenerate,
  onRevoke,
}: ShareLinkCardProps) {
  const isRevoked = link.status === "revoked";
  return (
    <div
      className="rounded-[22px] bg-background shadow-xs hover:shadow-sm transition-shadow p-5 flex items-start justify-between gap-4"
      style={{ border: "0.5px solid var(--hairline)" }}
      data-testid={`share-link-card-${link.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-[14px] font-semibold truncate">{link.label}</h3>
          <Badge variant={statusVariant(link.status)} className="text-[11px]">
            {statusLabel(link.status)}
          </Badge>
          <Badge variant="outline" className="text-[11px]">
            {link.scope === "report" ? "Report" : "Full Dashboard"}
          </Badge>
          {link.passwordProtected && (
            <Badge
              variant="outline"
              className="text-[11px] gap-1 inline-flex items-center"
              title="Password protected"
            >
              <Lock size={11} />
              Protected
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-[12px] text-muted-foreground">
          <span className="font-mono">{link.tokenPrefix}…</span>
          <span>{formatExpirationLabel(link.expiresAt)}</span>
          <span className="inline-flex items-center gap-1">
            <Eye size={12} />
            {link.accessCount} {link.accessCount === 1 ? "view" : "views"}
          </span>
        </div>
      </div>

      {canManage && !isRevoked && (
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="rounded-2xl text-[13px] cursor-pointer"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={onEdit}
            title="Edit metadata"
            aria-label={`Edit share link "${link.label}"`}
            data-testid={`edit-${link.id}`}
          >
            <PencilSimple size={14} className="mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-2xl text-[13px] cursor-pointer"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={onRegenerate}
            title="Generate a new URL — old URL will stop working"
            aria-label={`Regenerate URL for share link "${link.label}"`}
            data-testid={`regenerate-${link.id}`}
          >
            <ArrowsClockwise size={14} className="mr-1" />
            Regenerate
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-2xl text-[13px] cursor-pointer text-destructive"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={onRevoke}
            title="Revoke this link"
            aria-label={`Revoke share link "${link.label}"`}
            data-testid={`revoke-${link.id}`}
          >
            <Trash size={14} className="mr-1" />
            Revoke
          </Button>
        </div>
      )}
    </div>
  );
}
